/**
 * Re-engagement Campaign Templates
 * Based on best practices from HubSpot, Outreach, Gong, Close.io
 *
 * Key insights from sales re-engagement experts:
 * - 15-20% of lost deals can be revived with the right approach
 * - Timing changes are the #1 reason for revival success
 * - New triggers (leadership change, funding, etc.) create reopening opportunities
 * - Ghosted prospects often respond to "breakup" emails
 * - Past customers are 5x more likely to buy than new prospects
 */

import { CampaignTemplate } from "./types";

export const REENGAGE_LOST_DEAL: CampaignTemplate = {
  id: "reengage-lost-deal",
  name: "Lost Deal Revival",
  category: "reengagement",
  description: "Re-engage prospects from deals that were lost or went cold.",
  strategy:
    "Based on Gong's analysis that 15-20% of lost deals can be revived when circumstances change. Focus on what's changed since the deal closed - new product features, their situation, market conditions, or a new trigger event.",
  icon: "UndoOutlined",
  color: "#ef4444",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "10-15% re-engagement rate",
    "Revived pipeline from closed-lost",
    "Valuable competitive intelligence",
    "Clear win/loss understanding",
  ],

  bestPractices: [
    "Wait at least 3-6 months after deal closed",
    "Lead with what's changed, not 'checking in'",
    "Reference their original evaluation criteria",
    "Don't be defensive about why they didn't choose you",
    "Offer low-commitment re-engagement option",
  ],

  objectionHandling: [
    "'We went with [competitor]' → How's that going? Happy to share what's new",
    "'Nothing has changed' → Here's what's changed on our end...",
    "'Still not the right time' → When should I check back?",
  ],

  segmentation: {
    leadSource: ["crm", "manual"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - What's Changed",
      subject: "{{first_name}}, a lot has changed since we last talked",
      suggestedMjmlTemplateId: "coldOutreach-lostDealRevival",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Reconnect by highlighting what's changed since they evaluated",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "time_since",
          defaultValue: "6 months",
          description: "Time since deal closed",
        },
        {
          key: "what_changed",
          defaultValue: "we've launched several features you asked about",
          description: "Key change since their evaluation",
        },
        {
          key: "new_feature_1",
          defaultValue: "AI-powered conversation routing",
          description: "First new feature",
        },
        {
          key: "new_feature_2",
          defaultValue: "Advanced analytics dashboard",
          description: "Second new feature",
        },
      ],
    },
    {
      name: "Day 5 - Case Study",
      subject: "{{case_study_company}} switched - here's why",
      suggestedMjmlTemplateId: "coldOutreach-competitorSwitch",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share case study of competitor customer who switched",
      variables: [
        {
          key: "case_study_company",
          defaultValue: "A company like yours",
          description: "Reference customer who switched",
        },
        {
          key: "switch_reason",
          defaultValue: "they weren't getting the results they expected",
          description: "Why they switched",
        },
        {
          key: "switch_result",
          defaultValue: "40% improvement in first 90 days",
          description: "Results after switching",
        },
      ],
    },
    {
      name: "Day 11 - Quick Check",
      subject: "How's {{competitor}} working out?",
      suggestedMjmlTemplateId: "coldOutreach-competitorCheck",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Genuinely check on their current solution satisfaction",
      variables: [
        {
          key: "competitor",
          defaultValue: "your current solution",
          description: "Solution they chose instead",
        },
      ],
    },
    {
      name: "Day 18 - Low Commitment",
      subject: "No commitment - just a fresh look?",
      suggestedMjmlTemplateId: "coldOutreach-lowCommitment",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Offer low-commitment way to re-evaluate",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "competitor",
      defaultValue: "your current solution",
      description: "Solution they chose",
      source: "crm",
    },
    {
      key: "original_pain",
      defaultValue: "customer support efficiency",
      description: "Original pain point they had",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["reengagement", "lost-deal", "revival", "closed-lost", "winback"],
  bestFor: ["Pipeline Revival", "Competitive Intelligence", "Q4 Push"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Gong", "Close.io", "Outreach"],
};

export const REENGAGE_DORMANT_LEAD: CampaignTemplate = {
  id: "reengage-dormant-lead",
  name: "Dormant Lead Reactivation",
  category: "reengagement",
  description: "Wake up leads who showed interest but went quiet.",
  strategy:
    "Based on HubSpot's research that leads often go dormant due to timing, not lack of interest. Use a pattern interrupt approach with creative subject lines and offers to re-capture attention.",
  icon: "BellOutlined",
  color: "#f59e0b",
  targetAudience: "generic",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "15-25% re-engagement rate",
    "Pipeline recovery from nurture database",
    "Clear segmentation of active vs. dead leads",
    "Reduced database decay",
  ],

  bestPractices: [
    "Use pattern-interrupt subject lines",
    "Don't blame them for going quiet",
    "Offer something new/different than before",
    "Make it easy to signal interest or opt out",
    "Segment based on engagement level",
  ],

  segmentation: {
    leadSource: ["website", "linkedin", "event", "purchased"],
    companySize: ["11-50", "51-200", "201-500"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Pattern Interrupt",
      subject: "{{first_name}}, did I lose you?",
      suggestedMjmlTemplateId: "coldOutreach-patternInterrupt",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Break through inbox clutter with unexpected subject",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "last_engagement",
          defaultValue: "downloaded our guide",
          description: "What they last engaged with",
        },
        {
          key: "time_ago",
          defaultValue: "a few months ago",
          description: "When they last engaged",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "Should I stop emailing you?",
          "{{first_name}}, still interested?",
          "Where'd you go, {{first_name}}?",
        ],
      },
    },
    {
      name: "Day 4 - New Value",
      subject: "Something new since we last talked",
      suggestedMjmlTemplateId: "coldOutreach-newValue",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer something new that might reignite interest",
      variables: [
        {
          key: "new_value",
          defaultValue: "a case study showing 40% ROI in 90 days",
          description: "New value to offer",
        },
      ],
    },
    {
      name: "Day 9 - Easy Options",
      subject: "1, 2, or 3?",
      suggestedMjmlTemplateId: "coldOutreach-easyOptions",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Give simple numbered options to respond",
      variables: [
        {
          key: "option_1",
          defaultValue: "Still interested, just busy",
          description: "First option",
        },
        {
          key: "option_2",
          defaultValue: "Not a priority right now",
          description: "Second option",
        },
        {
          key: "option_3",
          defaultValue: "Please stop emailing",
          description: "Third option (opt out)",
        },
      ],
    },
    {
      name: "Day 15 - Final Attempt",
      subject: "Last email from me, {{first_name}}",
      suggestedMjmlTemplateId: "coldOutreach-finalAttempt",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Final outreach with clear last-chance messaging",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 60,
    dailyLimit: 180,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["reengagement", "dormant", "nurture", "database-reactivation"],
  bestFor: ["Database Cleanup", "Pipeline Generation", "Marketing Leads"],
  difficulty: "beginner",
  setupTime: "15 minutes",
  inspiredBy: ["HubSpot", "Mailchimp", "Close.io"],
};

export const REENGAGE_PAST_CUSTOMER: CampaignTemplate = {
  id: "reengage-past-customer",
  name: "Win-Back Past Customer",
  category: "reengagement",
  description: "Re-engage customers who churned or didn't renew.",
  strategy:
    "Based on research showing past customers are 5x more likely to buy than new prospects. Address why they left, show what's improved, and offer a compelling reason to return.",
  icon: "HeartOutlined",
  color: "#ec4899",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "15-25% response rate",
    "10-15% return rate",
    "Valuable churn feedback",
    "Product improvement insights",
  ],

  bestPractices: [
    "Acknowledge why they left without being defensive",
    "Show specific improvements since they churned",
    "Offer a return incentive (discount, free period)",
    "Don't guilt them - keep it positive",
    "Ask for feedback even if they don't return",
  ],

  objectionHandling: [
    "'We found a better solution' → What were they better at? We've improved...",
    "'Too expensive' → We've updated pricing/added more value",
    "'Product didn't meet needs' → Here's what we've built since...",
    "'Bad experience' → We've changed [specific thing], here's proof",
  ],

  segmentation: {
    leadSource: ["crm", "manual"],
    companySize: ["11-50", "51-200", "201-500"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - We Miss You",
      subject: "{{first_name}}, we've been making changes",
      suggestedMjmlTemplateId: "coldOutreach-weMissYou",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Reconnect and acknowledge their departure",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "time_since_churn",
          defaultValue: "6 months",
          description: "Time since they left",
        },
        {
          key: "improvement_1",
          defaultValue: "completely redesigned our interface",
          description: "First improvement made",
        },
        {
          key: "improvement_2",
          defaultValue: "added the integrations you asked for",
          description: "Second improvement made",
        },
      ],
    },
    {
      name: "Day 5 - What's New",
      subject: "Here's what's new since you left",
      suggestedMjmlTemplateId: "coldOutreach-whatsNew",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Detailed showcase of improvements and new features",
      variables: [
        {
          key: "new_feature_1",
          defaultValue: "AI-powered automation",
          description: "First new feature",
        },
        {
          key: "new_feature_2",
          defaultValue: "Advanced reporting",
          description: "Second new feature",
        },
        {
          key: "new_feature_3",
          defaultValue: "Improved mobile app",
          description: "Third new feature",
        },
      ],
    },
    {
      name: "Day 11 - Special Offer",
      subject: "Special offer for returning customers",
      suggestedMjmlTemplateId: "coldOutreach-winbackOffer",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Present return incentive",
      variables: [
        {
          key: "return_offer",
          defaultValue: "3 months free on your first year back",
          description: "Return incentive",
        },
        {
          key: "offer_expiry",
          defaultValue: "end of month",
          description: "When offer expires",
        },
      ],
    },
    {
      name: "Day 18 - Feedback Request",
      subject: "Can I ask you one thing, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-feedbackRequest",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Ask for feedback even if they don't return",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Customer success or sales rep name",
      source: "crm",
    },
    {
      key: "churn_reason",
      defaultValue: "the timing wasn't right",
      description: "Why they churned (if known)",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["reengagement", "winback", "churned", "past-customer", "retention"],
  bestFor: ["Customer Success Teams", "Retention Marketing", "Win-Back Campaigns"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Gainsight", "ChurnZero", "Intercom"],
};

export const REENGAGE_GHOSTED: CampaignTemplate = {
  id: "reengage-ghosted",
  name: "Ghosted Prospect Recovery",
  category: "reengagement",
  description: "Re-engage prospects who stopped responding mid-conversation.",
  strategy:
    "Based on Outreach data showing that ghosted prospects often respond to 'breakup' emails. Use humor and directness to break through, acknowledge the ghost, and offer a fresh start.",
  icon: "EyeInvisibleOutlined",
  color: "#6366f1",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "25-40% open rate on breakup email",
    "10-15% response to direct approach",
    "Clear yes/no for pipeline hygiene",
    "Insights into why they went silent",
  ],

  bestPractices: [
    "Use humor - don't be upset or passive-aggressive",
    "Reference your previous conversation specifically",
    "Make it easy to say 'not interested'",
    "Offer alternative timing if now isn't right",
    "Keep emails very short",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["11-50", "51-200", "201-500"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Acknowledge Ghost",
      subject: "{{first_name}}, did I say something wrong?",
      suggestedMjmlTemplateId: "coldOutreach-acknowledgeGhost",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Humorous acknowledgment that they've gone silent",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "last_topic",
          defaultValue: "our demo",
          description: "What you last discussed",
        },
        {
          key: "days_since",
          defaultValue: "two weeks",
          description: "Time since last contact",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "Did I get ghosted?",
          "{{first_name}}, are you okay?",
          "I'm starting to worry, {{first_name}}",
        ],
      },
    },
    {
      name: "Day 4 - Timing Check",
      subject: "Is it timing, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-timingCheck",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Check if timing is the issue",
      variables: [
        {
          key: "future_check",
          defaultValue: "next quarter",
          description: "Suggested future check-in time",
        },
      ],
    },
    {
      name: "Day 9 - One Word Reply",
      subject: "Just reply '1' or '2'",
      suggestedMjmlTemplateId: "coldOutreach-oneWordReply",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Make it incredibly easy to respond",
      variables: [
        {
          key: "option_1",
          defaultValue: "Interested, just busy",
          description: "First option meaning",
        },
        {
          key: "option_2",
          defaultValue: "Not interested",
          description: "Second option meaning",
        },
      ],
    },
    {
      name: "Day 14 - Breakup",
      subject: "I'll take the hint",
      suggestedMjmlTemplateId: "coldOutreach-takeTheHint",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Final breakup email - often gets highest response",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 60,
    dailyLimit: 180,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "last_topic",
      defaultValue: "our conversation",
      description: "What you last discussed",
      source: "crm",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["reengagement", "ghosted", "breakup", "pipeline-hygiene", "humor"],
  bestFor: ["SDR Teams", "Pipeline Cleanup", "High-Volume Sales"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  inspiredBy: ["Outreach", "Reply.io", "Yesware"],
};

export const REENGAGEMENT_TEMPLATES = [
  REENGAGE_LOST_DEAL,
  REENGAGE_DORMANT_LEAD,
  REENGAGE_PAST_CUSTOMER,
  REENGAGE_GHOSTED,
];
